import { useEffect, useState, useRef } from 'react';
import VideoPlayer from "src/components/VideoPlayer";
import { styled } from '@mui/material/styles';
import Page from '../components/Page';
import Logo from '../components/Logo';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useLocation } from 'react-router-dom';

const RootStyle = styled('div')(({ theme }) => ({
  height: '100vh', // lock to viewport height
  overflow: 'hidden', // prevent scroll
  backgroundImage: 'url("/static/vidisky_bg.jpg")',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'space-between', // distribute logo & video evenly
  padding: theme.spacing(2, 0),
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  flexShrink: 0, // prevent resizing
  marginBottom:theme.spacing(2),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  paddingTop: theme.spacing(1),
}));

const VideoWrapper = styled(Box)(({ theme }) => ({
  flex: 1, // take remaining height
  width: '100%',
  maxWidth: '1400px',
  borderRadius: theme.shape.borderRadius * 2,
  overflow: 'hidden',
  boxShadow: '0 0 20px rgba(0,0,0,0.5)',
  backgroundColor: '#000',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',

  '& .video-container': {
    width: '100% !important',
    height: '100% !important',
    maxWidth: '100% !important',
  },

  '& .video-js': {
    width: '100% !important',
    height: '100% !important',
    objectFit: 'contain !important',
  },

  [theme.breakpoints.down('sm')]: {
    '& .video-js': {
      height: '100% !important',
    },
  },
}));

const ErrorMessage = styled(Typography)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  alignItems: 'top',
  justifyContent: 'center',
  color: '#fff',
  fontSize: '2rem !important',
  textAlign: 'center',
  marginTop:theme.spacing(2),
}));

// ----------------------------------------------------------------------

export default function EscalatedVideo() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  let videoUrl = queryParams.get('video');

  // if (!videoUrl || typeof videoUrl !== 'string' || !/^https?:\/\//.test(videoUrl)) {
  //   videoUrl = "";
  // }

  const [validVideo, setValidVideo] = useState(false);
  const [loading, setLoading] = useState(true);
  const playerRef = useRef(null);

    // ✅ Smart validation
  useEffect(() => {
    const validateVideo = async () => {
      if (!videoUrl || !/^https?:\/\//.test(videoUrl)) {
        setValidVideo(false);
        setLoading(false);
        return;
      }

      const extensionCheck = /\.(mp4|webm|mov|mkv|avi|flv|m3u8)$/i.test(videoUrl);
      const isHls = /\.m3u8($|\?)/i.test(videoUrl);
      if (isHls) { setValidVideo(true); setLoading(false); return; }

      try {
        const response = await fetch(videoUrl, { method: 'HEAD' });
        const contentType = response.headers.get('content-type');

        if (response.ok && contentType?.startsWith('video/')) {
          setValidVideo(true);
        } else if (
          (response.status === 403 || response.status === 405) &&
          extensionCheck
        ) {
          // ✅ S3 or CDN fallback: allow if URL ends with a known video extension
          setValidVideo(true);
        } else {
          setValidVideo(false);
        }
      } catch (err) {
        console.warn('HEAD check failed, fallback to extension:', err);
        setValidVideo(extensionCheck);
      } finally {
        setLoading(false);
      }
    };

    validateVideo();
  }, [videoUrl]);


  return (
    <Page title="Escalated Video">
      <RootStyle>
        {/* Top logo */}
        <HeaderBox>
          <Logo />
        </HeaderBox>

        {loading ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress sx={{ color: '#fff' }} />
          </Box>
        ) : validVideo ? (
          <VideoWrapper>
            <VideoPlayer ref={playerRef} src={videoUrl} />
          </VideoWrapper>
        ) : (
          <ErrorMessage>Invalid Video Link</ErrorMessage>
        )}
      </RootStyle>
    </Page>
  );
}
